/**
 * useAntiSpoof Hook
 * =================
 * Loads the anti-spoofing ONNX model and runs inference
 * entirely in-browser via ONNX Runtime Web (WASM backend, no WebGL needed).
 *
 * Model: ~600KB quantized, input [1,3,128,128], output [1,2] (spoof, real)
 */

import { useEffect, useRef, useState, useCallback } from 'react';

const MODEL_URL = '/models/best_model_quantized.onnx';

// Softmax for 2-class output
function softmax(logits) {
  const max = Math.max(...logits);
  const exps = logits.map(x => Math.exp(x - max));
  const sum = exps.reduce((a, b) => a + b, 0);
  return exps.map(x => x / sum);
}

export function useAntiSpoof() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [error, setError] = useState(null);
  const sessionRef = useRef(null);
  const ortRef = useRef(null);
  const initAttempted = useRef(false);
  const cropCanvasRef = useRef(null);

  useEffect(() => {
    if (initAttempted.current) return;
    initAttempted.current = true;

    async function init() {
      try {
        setLoadProgress(20);

        // Dynamic import to avoid bundling WASM in main chunk
        const ort = await import('onnxruntime-web');
        ortRef.current = ort;
        setLoadProgress(50);

        // Force WASM backend (no WebGL dependency)
        ort.env.wasm.numThreads = 1;

        // Load anti-spoof model
        const session = await ort.InferenceSession.create(MODEL_URL, {
          executionProviders: ['wasm'],
        });
        sessionRef.current = session;

        setLoadProgress(100);
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to load anti-spoof model:', err);
        setError(err.message || 'Failed to load liveness detection model');
        setIsLoading(false);
      }
    }

    init();

    return () => {
      if (sessionRef.current) {
        sessionRef.current.release();
        sessionRef.current = null;
      }
    };
  }, []);

  /**
   * Run anti-spoof detection on a video frame.
   * Uses center crop (face oval guides user to center).
   *
   * @param {HTMLCanvasElement|HTMLVideoElement} source - Frame source
   * @returns {Promise<{score: number, isReal: boolean}|null>}
   */
  const detect = useCallback(async (source) => {
    if (!sessionRef.current || !ortRef.current) return null;

    const ort = ortRef.current;

    try {
      // Get source dimensions
      const srcWidth = source.videoWidth || source.width;
      const srcHeight = source.videoHeight || source.height;
      if (!srcWidth || !srcHeight) return null;

      // Create/reuse crop canvas (128x128)
      if (!cropCanvasRef.current) {
        cropCanvasRef.current = document.createElement('canvas');
        cropCanvasRef.current.width = 128;
        cropCanvasRef.current.height = 128;
      }
      const cropCanvas = cropCanvasRef.current;
      const ctx = cropCanvas.getContext('2d');

      // Center crop: take the middle 60% of the frame (where the face oval is)
      const cropSize = Math.min(srcWidth, srcHeight) * 0.6;
      const sx = (srcWidth - cropSize) / 2;
      const sy = (srcHeight - cropSize) / 2;

      // Draw center crop resized to 128x128
      ctx.drawImage(source, sx, sy, cropSize, cropSize, 0, 0, 128, 128);

      // Get pixel data
      const imageData = ctx.getImageData(0, 0, 128, 128);
      const pixels = imageData.data; // RGBA, uint8

      // Convert to float32 NCHW format [1, 3, 128, 128], BGR order, normalized [0, 1]
      // Model trained on OpenCV (BGR) images — swap R and B channels
      const floatData = new Float32Array(3 * 128 * 128);
      for (let i = 0; i < 128 * 128; i++) {
        const pixelOffset = i * 4;
        floatData[i] = pixels[pixelOffset + 2] / 255.0;                // B channel
        floatData[128 * 128 + i] = pixels[pixelOffset + 1] / 255.0;    // G channel
        floatData[2 * 128 * 128 + i] = pixels[pixelOffset] / 255.0;    // R channel
      }

      // Create input tensor
      const inputTensor = new ort.Tensor('float32', floatData, [1, 3, 128, 128]);

      // Use actual model input/output names
      const inputName = sessionRef.current.inputNames[0];
      const outputName = sessionRef.current.outputNames[0];

      // Run inference
      const feeds = {};
      feeds[inputName] = inputTensor;
      const results = await sessionRef.current.run(feeds);

      // Parse output: [1, 2] → [real_logit, spoof_logit]
      const output = results[outputName].data;
      const probs = softmax([output[0], output[1]]);
      const realScore = probs[0]; // First class = real

      // Threshold 0.03 - very lenient for mobile in any lighting
      return {
        score: realScore,
        isReal: realScore > 0.03,
      };
    } catch (err) {
      if (!detect._errorLogged) {
        console.warn('Anti-spoof inference error:', err.message || err);
        detect._errorLogged = true;
      }
      return null;
    }
  }, []);

  return { isLoading, loadProgress, error, detect };
}
